// Engine capabilities + product limits for the custom workflow builder.
//
// The schema (schema.ts) describes everything the graph model CAN express;
// this module describes what the generic interpreter currently EXECUTES and
// the caps the product enforces. The workflow-definitions edge function
// rejects publishes outside these bounds, and the builder UI uses the same
// lists to hide/disable what isn't runnable yet. One source of truth, so an
// owner can never publish a workflow the engine would silently drop.

import type {
  Binding,
  ExecutionAction,
  TriggerEvent,
  WorkflowDefinition,
  WorkflowNode,
} from "./schema.ts";
import {
  ACTION_LABELS,
  BOOKING_TRIGGER_EVENTS,
  CHANNEL_LABELS,
  loopSpans,
  normalizeExceptions,
  validateDefinition,
  type ValidationIssue,
  WAIT_EVENT_EVENTS,
} from "./schema.ts";
import { CONTEXT_FIELD_CATALOG, type ContextFieldDef } from "./context.ts";
import { decisionFieldDefs, parseDecisionField } from "./decisionFields.ts";
import { templateBodyFromText } from "./whatsappTemplateText.ts";

/** Trigger events the interpreter can start runs on today. */
export const SUPPORTED_TRIGGER_EVENTS: readonly TriggerEvent[] = [
  "trial_or_first_visit_completed",
  "booking_upcoming",
  "no_show",
  "booking_completed",
  "session_finished",
  "booking_created",
  "booking_cancelled",
  "form_submitted",
  "lead_replied_then_went_quiet",
  "no_reply_to_last_message",
  "message_received",
  "complaint_received",
  "case_opened",
  "case_acknowledged",
  "case_resolved",
  "case_overdue",
];

/** Execution actions the interpreter can dispatch today. Money-touching
 *  actions (refunds, payment links) are deliberately absent from the
 *  authorable vocabulary. They stay hand-coded with their safety rails.
 *  schedule_followup writes a durable scheduled_messages row (same rail as
 *  owner-scheduled sends; the every-minute sweep delivers it with send-time
 *  window gating).
 *
 *  This list and the `ExecutionAction` union are now in lockstep. The
 *  listed-but-dead create_offer / create_task were removed from the union
 *  rather than left to render in the picker as choices that never run. */
export const SUPPORTED_ACTIONS: readonly ExecutionAction[] = [
  "send_message",
  "request_feedback",
  "add_tag",
  "remove_tag",
  "save_to_profile",
  "fill_from_waitlist",
  "schedule_followup",
  "escalate_conversation",
  "cancel_booking",
  "reschedule_booking",
  "notify_owner",
  "open_case",
  "pause_outreach",
  "reply_to_complaint",
  "issue_recovery_offer",
];

/** Tally is a CRM/analytics intake source. A form-triggered workflow must not
 *  mutate Dibs Booking records, even if a legacy snapshot or NL draft tries
 *  to combine those otherwise-supported capabilities. */
const BOOKING_MUTATION_ACTIONS: readonly ExecutionAction[] = [
  "fill_from_waitlist",
  "cancel_booking",
  "reschedule_booking",
];

/** Widest authorable reschedule push. Beyond a month "move it back" stops
 *  being a reschedule and becomes a cancellation the customer didn't ask
 *  for, and the further out it lands, the less likely the availability the
 *  action checks at approval time still means anything. */
export const MAX_RESCHEDULE_DAYS = 30;

// ALWAYS_APPROVE_ACTIONS is declared in schema.ts (the display layer needs it
// and this module already imports from there) and re-exported here so callers
// keep getting the whole capability surface from one place.
export { ALWAYS_APPROVE_ACTIONS } from "./schema.ts";

// No separate channel-constraint list lives here on purpose: `onChannel` is an
// `options` param, and validateDefinition already rejects any value outside
// CHANNEL_LABELS. A second list would be a second source of truth to drift.

/** Flat per-business cap on non-archived custom workflows (v1; may split by
 *  plan tier later. See WORKFLOW_BUILDER_PLAN.md §8.1). */
export const MAX_ACTIVE_WORKFLOWS = 10;

/** Latest a Session finished trigger can fire after the session ends. */
export const MAX_SESSION_FINISHED_HOURS = 72;

/** Longest a note title may be (contact_notes allows 200). */
export const MAX_NOTE_TITLE_CHARS = 120;

/** Widest authorable late-cancel window (booking_cancelled's
 *  `cancelledWithinHours` filter). A week; beyond that "late" is meaningless. */
export const MAX_CANCEL_WINDOW_HOURS = 168;

/** Sanity bound on graph size. Keeps runs and the canvas tractable. */
export const MAX_NODES = 30;

// Agentic time bounds (plan §8.5, asymmetric authority)

/** The timing advisor may postpone a single delay node at most this many
 *  times before it must proceed or abandon. */
export const MAX_POSTPONEMENTS_PER_DELAY = 3;

/** Ceiling on any AI-written contact_outreach_holds duration. */
export const MAX_HOLD_DAYS = 14;

/** Ceiling on the per-node `maxExtensionMinutes` an owner can author. */
export const MAX_AI_EXTENSION_MINUTES = 14 * 24 * 60;

// Run-lifetime bounds
// Publish-gated ceilings on authored waits keep every run's total lifetime
// bounded, which is what makes the stuck-run reaper (customWorkflowSweep)
// SOUND: any run still 'running' past the ceiling is dead with certainty,
// never a legitimate long sleep.

/** Longest single authored Wait (duration or until-event timeout). */
export const MAX_DELAY_MINUTES = 30 * 24 * 60;

/** Cap on the SUM of authored waits across one workflow. */
export const MAX_TOTAL_DELAY_MINUTES = 60 * 24 * 60;

/** Runs still 'running' after this many days are reaped as failed. Ceiling =
 *  60d total authored waits + bounded AI postponements, with generous slack. */
export const MAX_RUN_LIFETIME_DAYS = 120;

// Loop ("Repeat until") bounds

/** Most rounds one loop may run. The engine clamps to this too. */
export const MAX_LOOP_ITERATIONS = 10;

/** Most blocks one loop may repeat. Keeps a round readable and its cost known. */
export const MAX_LOOP_BODY_BLOCKS = 8;

/** Shortest guaranteed gap between two rounds, from the loop's own duration
 *  Waits. Stops a loop from spinning. */
export const MIN_LOOP_ROUND_MINUTES = 60;

/** Shortest gap between rounds when a round messages the customer. A run's
 *  own earlier sends don't count against the engine's cross-workflow
 *  frequency cap, so a loop gets the same spacing here instead. Mirrors
 *  ENGINE_MIN_HOURS_BETWEEN_SENDS in guards.ts (imported there by a test;
 *  guards.ts is Deno-only, and this module also ships to the builder). */
export const MIN_LOOP_CUSTOMER_ROUND_MINUTES = 24 * 60;

/** The actions that make sense repeated. Everything else is a one-time act
 *  (cancel, reschedule, release a slot, issue a code, open a case, reply
 *  publicly, hand over) that must never happen once per round. */
export const LOOP_ALLOWED_ACTIONS: readonly ExecutionAction[] = [
  "send_message",
  "add_tag",
  "remove_tag",
  "notify_owner",
];

/** Inngest ends a run at 1,000 steps. Publish keeps the worst case well under
 *  it, so a loop can never be cut off halfway through a round. */
export const MAX_RUN_STEPS = 800;

/** A business_message_templates id. Whose template it is, and whether Meta
 *  approved it, is checked at send time; publish only checks the shape. */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Inngest steps one pass through a block can take, at most. Mirrors the
 *  runner's step.* calls per case. */
function blockSteps(n: WorkflowNode): number {
  switch (n.type) {
    case "trigger":
      // Session finished sleeps until its moment before anything runs.
      return n.parameters.event === "session_finished" ? 2 : 0;
    case "condition":
      return 2; // the check + ending the run
    case "decision":
    case "execution":
      return 1;
    case "delay":
      if (n.parameters.mode === "until_event") return 1;
      // reply check + wait + settle
      if (n.parameters.mode === "until_reply") return 3;
      // target + sleep + end, and every AI timing review with its sleep
      return 3 +
        (n.parameters.aiAdjustable === true
          ? (MAX_POSTPONEMENTS_PER_DELAY + 1) * 2
          : 0);
    default:
      return 0;
  }
}

/** The most Inngest steps one run of this workflow can take, counting every
 *  loop at its full number of rounds. Exported for tests. */
export function estimateMaxRunSteps(def: WorkflowDefinition): number {
  const spans = loopSpans(def);
  let total = 3; // claim the run, log its start, end it
  def.nodes.forEach((n, i) => {
    const span = spans.find((sp) => i >= sp.startIndex && i <= sp.endIndex);
    if (span) {
      const loopNode = def.nodes[span.loopIndex];
      total += blockSteps(n) * loopRounds(loopNode);
    } else if (n.type === "loop") {
      total += loopRounds(n) + 1; // one check per round, plus the final one
    } else {
      total += blockSteps(n);
    }
  });
  return total;
}

/** A loop's round limit as the engine will run it. */
export function loopRounds(n: WorkflowNode): number {
  const raw = Number(n.parameters.maxIterations);
  if (!Number.isFinite(raw)) return 1;
  return Math.min(MAX_LOOP_ITERATIONS, Math.max(1, Math.floor(raw)));
}

/** Rule-shape checks shared by Condition exceptions and loop stop rules.
 *  Predicates the engine can't evaluate fail OPEN at runtime (an unknown
 *  field never trips), so an unevaluable rule must never reach publish. */
function predicateIssues(
  def: WorkflowDefinition,
  nodeIndex: number,
  raw: unknown,
  noun: string,
): ValidationIssue[] {
  const nodeId = def.nodes[nodeIndex].id;
  const issues: ValidationIssue[] = [];
  // AI step results are only known once that step has run, above this one.
  let decisionFields: ContextFieldDef[] | null = null;
  for (const group of normalizeExceptions(raw)) {
    for (const p of group.all) {
      let fieldDef = CONTEXT_FIELD_CATALOG.find((f) => f.field === p.field);
      if (!fieldDef && parseDecisionField(p.field)) {
        decisionFields ??= decisionFieldDefs(def, nodeIndex);
        fieldDef = decisionFields.find((f) => f.field === p.field);
        if (!fieldDef) {
          issues.push({
            nodeId,
            message:
              `${noun} checks an AI step's result that doesn't exist above it. Pick the AI step and result again.`,
          });
          continue;
        }
      }
      if (!fieldDef) {
        issues.push({
          nodeId,
          message: `${noun} checks "${p.field}", which the engine can't evaluate.`,
        });
        continue;
      }
      if (!fieldDef.operators.includes(p.operator)) {
        issues.push({
          nodeId,
          message: `"${p.operator}" doesn't apply to ${fieldDef.label} — use ${
            fieldDef.operators.join(" / ")
          }.`,
        });
        continue;
      }
      const needsNumber = ["gt", "gte", "lt", "lte", "within_days"]
        .includes(p.operator);
      const needsValue = needsNumber ||
        ["eq", "neq", "in", "contains"].includes(p.operator);
      if (
        needsValue &&
        (p.value === undefined || p.value === null || p.value === "")
      ) {
        issues.push({
          nodeId,
          message: `"${
            group.label || fieldDef.label
          }" is missing its comparison value.`,
        });
      } else if (needsNumber && typeof p.value !== "number") {
        issues.push({
          nodeId,
          message: `"${
            group.label || fieldDef.label
          }" needs a number to compare against.`,
        });
      }
    }
  }
  return issues;
}

/**
 * Publish-time validation: structural checks (validateDefinition) PLUS the
 * engine-capability checks above. Returns [] when the definition is
 * publishable. Drafts may fail these freely. Only Publish is gated.
 */
export function validateForPublish(def: WorkflowDefinition): ValidationIssue[] {
  const issues = validateDefinition(def);
  const triggerEvent = def.nodes.find((node) => node.type === "trigger")
    ?.parameters.event as TriggerEvent | undefined;

  if (def.nodes.length > MAX_NODES) {
    issues.push({ message: `Workflows are limited to ${MAX_NODES} blocks.` });
  }

  def.nodes.forEach((n, nodeIndex) => {
    if (n.type === "trigger") {
      const event = n.parameters.event as TriggerEvent;
      if (event && !SUPPORTED_TRIGGER_EVENTS.includes(event)) {
        issues.push({
          nodeId: n.id,
          message: `Trigger "${event}" isn't supported yet.`,
        });
      }
      // Trigger-level filters only make sense on the events that carry the
      // data they test. Publish-gate the mismatch so the engine never has to.
      const listingId = n.parameters.listingId;
      if (
        listingId != null &&
        (typeof listingId !== "string" || listingId.length === 0)
      ) {
        issues.push({
          nodeId: n.id,
          message: "The service filter needs a service selected.",
        });
      } else if (
        typeof listingId === "string" && event &&
        !BOOKING_TRIGGER_EVENTS.includes(event)
      ) {
        issues.push({
          nodeId: n.id,
          message:
            `A service filter doesn't apply to "${event}" — only booking triggers.`,
        });
      }
      const formId = n.parameters.formId;
      if (
        formId != null &&
        (typeof formId !== "string" || formId.length === 0)
      ) {
        issues.push({
          nodeId: n.id,
          message: "The Tally form filter needs a form selected.",
        });
      } else if (
        typeof formId === "string" && event !== "form_submitted"
      ) {
        issues.push({
          nodeId: n.id,
          message:
            `A Tally form filter doesn't apply to "${event}" — only the Tally form submitted trigger.`,
        });
      }
      const cancelWindow = n.parameters.cancelledWithinHours;
      if (cancelWindow != null) {
        if (event !== "booking_cancelled") {
          issues.push({
            nodeId: n.id,
            message:
              `"Cancelled within" only applies to the Booking cancelled trigger.`,
          });
        } else if (
          typeof cancelWindow !== "number" || !Number.isFinite(cancelWindow) ||
          cancelWindow < 1 || cancelWindow > MAX_CANCEL_WINDOW_HOURS
        ) {
          issues.push({
            nodeId: n.id,
            message:
              `"Cancelled within" must be between 1 and ${MAX_CANCEL_WINDOW_HOURS} hours.`,
          });
        }
      }
      // message_received's own scopes. Gated the same way as the filters
      // above: a snapshot carrying them on another trigger is an authoring
      // mistake the engine would silently ignore, so reject it at publish.
      const messageChannel = n.parameters.messageChannel;
      if (messageChannel != null && messageChannel !== "any") {
        if (event !== "message_received") {
          issues.push({
            nodeId: n.id,
            message:
              `A channel filter only applies to the "Someone messages you" trigger.`,
          });
        } else if (
          typeof messageChannel !== "string" ||
          !(messageChannel in CHANNEL_LABELS)
        ) {
          issues.push({
            nodeId: n.id,
            message: `"On channel" isn't a channel Dibs can receive on.`,
          });
        }
      }
      if (n.parameters.newSenderOnly != null && event !== "message_received") {
        issues.push({
          nodeId: n.id,
          message:
            `"New enquiries only" only applies to the "Someone messages you" trigger.`,
        });
      }
      const hoursAfter = n.parameters.hoursAfter;
      if (hoursAfter != null) {
        if (event !== "session_finished") {
          issues.push({
            nodeId: n.id,
            message: `"Hours after it ends" only applies to the Session finished trigger.`,
          });
        } else if (
          typeof hoursAfter !== "number" || !Number.isFinite(hoursAfter) ||
          hoursAfter < 0 || hoursAfter > MAX_SESSION_FINISHED_HOURS
        ) {
          issues.push({
            nodeId: n.id,
            message:
              `"Hours after it ends" must be between 0 and ${MAX_SESSION_FINISHED_HOURS}.`,
          });
        }
      }
      if (n.parameters.firstSessionOnly != null && event !== "session_finished") {
        issues.push({
          nodeId: n.id,
          message: `"First session only" only applies to the Session finished trigger.`,
        });
      }
    }
    if (n.type === "condition") {
      // This closes the drafter loophole: the builder's pickers can't produce
      // bad predicates, but the NL drafter could.
      issues.push(
        ...predicateIssues(def, nodeIndex, n.parameters.exceptions, "Condition"),
      );
    }
    if (n.type === "loop") {
      issues.push(
        ...predicateIssues(def, nodeIndex, n.parameters.stopWhen, "This loop"),
      );
    }
    if (n.type === "execution") {
      const action = n.parameters.action as ExecutionAction;
      if (
        triggerEvent === "form_submitted" &&
        BOOKING_MUTATION_ACTIONS.includes(action)
      ) {
        issues.push({
          nodeId: n.id,
          message: `"${
            ACTION_LABELS[action]
          }" cannot run from a Tally form. Tally workflows are limited to CRM, analytics, messaging, and internal follow-up actions.`,
        });
      }
      // `ACTION_LABELS[action] &&` keeps this from double-reporting: an action
      // outside the union entirely (a legacy `create_offer` snapshot) is
      // already rejected by validateDefinition's generic options check. This
      // branch exists for the case only it can catch. An action the model can
      // express but the interpreter can't yet dispatch.
      if (
        action && ACTION_LABELS[action] && !SUPPORTED_ACTIONS.includes(action)
      ) {
        issues.push({
          nodeId: n.id,
          message: `Action "${action}" isn't supported yet.`,
        });
      }
      if (
        action === "send_message" || action === "request_feedback" ||
        action === "schedule_followup"
      ) {
        if (n.parameters.recipient !== "workflow_subject") {
          issues.push({
            nodeId: n.id,
            message: `"${
              ACTION_LABELS[action]
            }" must name its recipient. Messages can only go to the customer this workflow started for.`,
          });
        }
      }
      if (
        action === "send_message" || action === "request_feedback" ||
        action === "schedule_followup" || action === "reply_to_complaint" ||
        action === "save_to_profile"
      ) {
        const binding = n.parameters.message as unknown as Binding | undefined;
        if (
          !binding ||
          (binding.source !== "static" && binding.source !== "decision")
        ) {
          issues.push({
            nodeId: n.id,
            message: `"${ACTION_LABELS[action]}" needs message content.`,
          });
        } else if (
          binding.source === "static" &&
          (typeof binding.value !== "string" || !binding.value.trim())
        ) {
          issues.push({
            nodeId: n.id,
            message: `"${ACTION_LABELS[action]}" can't have an empty message.`,
          });
        }
      }
      // Automatic templates: Dibs submits the step's fixed wording to Meta,
      // so it must be fixed text in a shape Meta accepts.
      const autoTemplate = action === "send_message" &&
        n.parameters.whatsappTemplateAuto === true;
      if (autoTemplate) {
        const binding = n.parameters.message as unknown as Binding | undefined;
        if (binding?.source === "decision") {
          issues.push({
            nodeId: n.id,
            message:
              `An automatic WhatsApp template needs fixed text. AI-written messages change every time, and Meta only approves fixed wording.`,
          });
        } else if (
          binding?.source === "static" && typeof binding.value === "string" &&
          binding.value.trim()
        ) {
          const converted = templateBodyFromText(binding.value);
          if (converted.ok === false) {
            issues.push({ nodeId: n.id, message: converted.issue });
          }
        }
      }
      // A template only ever stands in for this step's message on WhatsApp,
      // so a step pinned to another channel could never use it.
      const templateId = n.parameters.whatsappTemplateId;
      if (
        action === "send_message" &&
        ((templateId != null && templateId !== "") || autoTemplate)
      ) {
        if (
          templateId != null && templateId !== "" &&
          (typeof templateId !== "string" || !UUID_RE.test(templateId))
        ) {
          issues.push({
            nodeId: n.id,
            message: `Pick the WhatsApp template from the list again.`,
          });
        }
        const only = String(n.parameters.onChannel ?? "any");
        if (only !== "any" && only !== "whatsapp") {
          issues.push({
            nodeId: n.id,
            message:
              `A WhatsApp template only applies to WhatsApp conversations. Set "Only on" to WhatsApp or Any channel, or remove the template.`,
          });
        }
      }
      if (action === "request_feedback") {
        const expires = Number(n.parameters.feedbackExpiresDays ?? 14);
        if (!Number.isFinite(expires) || expires < 1 || expires > 30) {
          issues.push({
            nodeId: n.id,
            message:
              `A private feedback link must expire between 1 and 30 days.`,
          });
        }
        if (n.parameters.requiresApproval === true) {
          issues.push({
            nodeId: n.id,
            message:
              `Private feedback requests cannot wait in the approval queue. Review the draft before publishing, then leave approval off.`,
          });
        }
      }
      // Deliberately STRICTER than the engine, which falls back to the node
      // label as tag text for legacy snapshots. That fallback is a safety net,
      // not a feature. Silently tagging someone "Tag at-risk customers"
      // (the block's name) is never what the owner meant, and for remove_tag
      // it would match nothing and skip every run.
      if (
        (action === "add_tag" || action === "remove_tag") &&
        !String(n.parameters.tag ?? "").trim()
      ) {
        issues.push({
          nodeId: n.id,
          message: `"${ACTION_LABELS[action]}" needs a tag.`,
        });
      }
      if (action === "save_to_profile" && n.parameters.requiresApproval === true) {
        issues.push({
          nodeId: n.id,
          message: `"Save to customer profile" can't wait for approval. It only adds a note for your team.`,
        });
      }
      if (
        action === "save_to_profile" &&
        String(n.parameters.noteTitle ?? "").length > MAX_NOTE_TITLE_CHARS
      ) {
        issues.push({
          nodeId: n.id,
          message: `A note title can be at most ${MAX_NOTE_TITLE_CHARS} characters.`,
        });
      }
      if (action === "reschedule_booking") {
        const days = n.parameters.rescheduleByDays;
        if (days == null) {
          issues.push({
            nodeId: n.id,
            message: `"Move the booking" needs to know how far to move it.`,
          });
        } else if (
          typeof days !== "number" || !Number.isFinite(days) || days < 1 ||
          days > MAX_RESCHEDULE_DAYS
        ) {
          issues.push({
            nodeId: n.id,
            message:
              `"Move back by" must be between 1 and ${MAX_RESCHEDULE_DAYS} days.`,
          });
        }
      }
      if (action === "schedule_followup") {
        const after = Number(n.parameters.followupDelayMinutes ?? 0);
        if (after > MAX_DELAY_MINUTES) {
          issues.push({
            nodeId: n.id,
            message: `A follow-up can't be scheduled more than ${
              MAX_DELAY_MINUTES / (24 * 60)
            } days out.`,
          });
        }
      }
      if (action === "open_case") {
        if (!String(n.parameters.reason ?? "").trim()) {
          issues.push({
            nodeId: n.id,
            message: `"Open a case" needs a factual case summary.`,
          });
        }
        if (!String(n.parameters.caseCategory ?? "").trim()) {
          issues.push({
            nodeId: n.id,
            message: `"Open a case" needs a category.`,
          });
        }
        if (!String(n.parameters.caseSeverity ?? "").trim()) {
          issues.push({
            nodeId: n.id,
            message: `"Open a case" needs an urgency.`,
          });
        }
        const ack = Number(n.parameters.acknowledgeWithinMinutes ?? 0);
        const resolution = Number(n.parameters.resolveWithinMinutes ?? 0);
        if (ack > 7 * 24 * 60) {
          issues.push({
            nodeId: n.id,
            message: `A case acknowledgement deadline can't exceed 7 days.`,
          });
        }
        if (resolution > 90 * 24 * 60) {
          issues.push({
            nodeId: n.id,
            message: `A case resolution deadline can't exceed 90 days.`,
          });
        }
        if (ack > 0 && resolution > 0 && resolution < ack) {
          issues.push({
            nodeId: n.id,
            message: `Resolution must be due after acknowledgement.`,
          });
        }
      }
      if (action === "pause_outreach") {
        if (!String(n.parameters.reason ?? "").trim()) {
          issues.push({
            nodeId: n.id,
            message:
              `"Pause customer outreach" needs a reason the team can see.`,
          });
        }
        const days = Number(n.parameters.holdDays ?? 30);
        if (!Number.isFinite(days) || days < 1 || days > 365) {
          issues.push({
            nodeId: n.id,
            message: `An outreach pause must be between 1 and 365 days.`,
          });
        }
      }
      if (
        action === "reply_to_complaint" || action === "issue_recovery_offer"
      ) {
        const triggerEvent = def.nodes.find((node) => node.type === "trigger")
          ?.parameters.event;
        if (triggerEvent !== "complaint_received") {
          issues.push({
            nodeId: n.id,
            message:
              `Complaint recovery actions can only be used in a workflow started by "Complaint received".`,
          });
        }
      }
    }
    if (n.type === "delay") {
      const mode = n.parameters.mode;
      if (mode === "duration") {
        const duration = n.parameters.durationMinutes;
        if (
          typeof duration !== "number" || !Number.isFinite(duration) ||
          duration <= 0
        ) {
          issues.push({
            nodeId: n.id,
            message: "A duration Wait needs a time greater than zero.",
          });
        }
      } else if (mode === "until_reply") {
        const timeout = n.parameters.timeoutMinutes;
        if (
          typeof timeout !== "number" || !Number.isFinite(timeout) ||
          timeout <= 0
        ) {
          issues.push({
            nodeId: n.id,
            message:
              "A wait-for-reply block needs a time limit greater than zero.",
          });
        }
      } else if (mode === "until_event") {
        const untilEvent = n.parameters.untilEvent as TriggerEvent | undefined;
        if (!untilEvent || !WAIT_EVENT_EVENTS.includes(untilEvent)) {
          issues.push({
            nodeId: n.id,
            message:
              "This Wait cannot be woken by the selected event. Choose a booking created, completed, cancelled, or no-show event.",
          });
        }
        const timeout = n.parameters.timeoutMinutes;
        if (
          typeof timeout !== "number" || !Number.isFinite(timeout) ||
          timeout <= 0
        ) {
          issues.push({
            nodeId: n.id,
            message:
              "A wait-until-event block needs a timeout greater than zero.",
          });
        }
      }
      const ext = n.parameters.maxExtensionMinutes;
      if (typeof ext === "number" && ext > MAX_AI_EXTENSION_MINUTES) {
        issues.push({
          nodeId: n.id,
          message: `AI timing extension can't exceed ${
            MAX_AI_EXTENSION_MINUTES / (24 * 60)
          } days.`,
        });
      }
      const wait = Number(
        n.parameters.durationMinutes ?? n.parameters.timeoutMinutes ?? 0,
      );
      if (wait > MAX_DELAY_MINUTES) {
        issues.push({
          nodeId: n.id,
          message: `A single Wait can't exceed ${
            MAX_DELAY_MINUTES / (24 * 60)
          } days.`,
        });
      }
    }
  });

  // Loops. What a round may contain, how far apart rounds are, and how many.
  const spans = loopSpans(def);
  for (const span of spans) {
    const loopNode = def.nodes[span.loopIndex];
    const body = def.nodes.slice(span.startIndex, span.endIndex + 1);
    if (normalizeExceptions(loopNode.parameters.stopWhen).length === 0) {
      issues.push({
        nodeId: loopNode.id,
        message:
          "Add at least one stop rule, so the loop knows when to stop repeating.",
      });
    }
    const rounds = Number(loopNode.parameters.maxIterations);
    if (
      !Number.isInteger(rounds) || rounds < 1 || rounds > MAX_LOOP_ITERATIONS
    ) {
      issues.push({
        nodeId: loopNode.id,
        message:
          `"Max rounds" must be a whole number from 1 to ${MAX_LOOP_ITERATIONS}.`,
      });
    }
    if (body.length > MAX_LOOP_BODY_BLOCKS) {
      issues.push({
        nodeId: loopNode.id,
        message:
          `A loop can repeat at most ${MAX_LOOP_BODY_BLOCKS} blocks. Move some below the loop.`,
      });
    }
    for (const inner of body) {
      const action = inner.parameters.action as ExecutionAction;
      if (inner.type === "execution" && !LOOP_ALLOWED_ACTIONS.includes(action)) {
        issues.push({
          nodeId: inner.id,
          message: `"${
            ACTION_LABELS[action] ?? action
          }" can't sit inside a loop — it would happen once every round. Move it below the loop.`,
        });
      }
    }
    // Only duration Waits guarantee time passes: an until-event Wait can wake
    // the moment it starts. AI timing may lengthen a Wait, never shorten it.
    const roundMinutes = body
      .filter((n) => n.type === "delay" && n.parameters.mode === "duration")
      .reduce((sum, n) => sum + Number(n.parameters.durationMinutes ?? 0), 0);
    const messagesCustomer = body.some((n) =>
      n.type === "execution" && n.parameters.action === "send_message"
    );
    const minimum = messagesCustomer
      ? MIN_LOOP_CUSTOMER_ROUND_MINUTES
      : MIN_LOOP_ROUND_MINUTES;
    if (roundMinutes < minimum) {
      issues.push({
        nodeId: loopNode.id,
        message: messagesCustomer
          ? `Each round messages the customer, so it needs a Wait of at least ${
            MIN_LOOP_CUSTOMER_ROUND_MINUTES / 60
          } hours inside the loop.`
          : `Each round needs a Wait of at least ${MIN_LOOP_ROUND_MINUTES} minutes inside the loop.`,
      });
    }
  }
  if (spans.length > 0 && estimateMaxRunSteps(def) > MAX_RUN_STEPS) {
    issues.push({
      message:
        "This workflow's loop would take too many steps to run safely. Lower its rounds or move blocks out of it.",
    });
  }

  // Total authored waiting time is capped so every run's lifetime is bounded
  // (the engine reaps runs past MAX_RUN_LIFETIME_DAYS as stuck). A Wait inside
  // a loop counts once per round.
  const totalWait = def.nodes.reduce((sum, n, i) => {
    if (n.type !== "delay") return sum;
    const span = spans.find((sp) => i >= sp.startIndex && i <= sp.endIndex);
    const rounds = span ? loopRounds(def.nodes[span.loopIndex]) : 1;
    return sum +
      rounds *
        Number(
          n.parameters.durationMinutes ?? n.parameters.timeoutMinutes ?? 0,
        );
  }, 0);
  if (totalWait > MAX_TOTAL_DELAY_MINUTES) {
    issues.push({
      message: `Total waiting time across the workflow can't exceed ${
        MAX_TOTAL_DELAY_MINUTES / (24 * 60)
      } days.`,
    });
  }

  // Bindings must point UPSTREAM: the runner walks nodes in order, so a Send
  // bound to a Decision that sits below it would always find no output and
  // silently skip. validateDefinition already checks the target exists.
  const indexOf = new Map(def.nodes.map((n, i) => [n.id, i] as const));
  for (const n of def.nodes) {
    if (n.type !== "execution") continue;
    const binding = n.parameters.message as unknown as Binding | undefined;
    if (binding?.source === "decision") {
      const from = indexOf.get(binding.nodeId);
      const self = indexOf.get(n.id) ?? 0;
      if (from !== undefined && from >= self) {
        issues.push({
          nodeId: n.id,
          message: `"${
            n.label ?? n.id
          }" uses an AI-written message, but its Decision block sits below it — move the Decision above the send.`,
        });
      }
    }
  }

  // Edges (when present) must reference real nodes and only point FORWARD.
  // The runner memoizes each node's Inngest step by id, so a backward edge
  // would replay stale results instead of re-executing. V1 has no loops.
  const skipSeen = new Set<string>();
  for (const e of def.edges ?? []) {
    const fromIdx = indexOf.get(e.from);
    if (fromIdx === undefined) {
      issues.push({ message: `Edge references unknown block "${e.from}".` });
      continue;
    }
    // Skip-edges are the Condition's "else". Only conditions have a skip
    // outcome, and each may route it exactly once.
    if (e.on === "skip") {
      if (def.nodes[fromIdx].type !== "condition") {
        issues.push({
          nodeId: e.from,
          message: 'Only Condition blocks can have a "skip to" branch.',
        });
      } else if (skipSeen.has(e.from)) {
        issues.push({
          nodeId: e.from,
          message: 'A Condition can only have one "skip to" branch.',
        });
      }
      skipSeen.add(e.from);
    }
    if (e.to === "end") continue;
    const toIdx = indexOf.get(e.to);
    if (toIdx === undefined) {
      issues.push({
        nodeId: e.from,
        message: `Edge points at unknown block "${e.to}".`,
      });
    } else if (toIdx <= fromIdx) {
      issues.push({
        nodeId: e.from,
        message:
          "Edges can only point forward. To repeat blocks, use a Repeat until block.",
      });
    } else {
      // A loop is entered only through its top, where the rounds are
      // counted. A jump from outside straight to a block inside would skip
      // the stop rules and the round limit.
      const target = spans.find((sp) =>
        toIdx >= sp.startIndex && toIdx <= sp.endIndex
      );
      if (target && !(fromIdx >= target.startIndex && fromIdx <= target.endIndex)) {
        issues.push({
          nodeId: e.from,
          message:
            "A skip can't jump into the middle of a loop. Point it at the Repeat until block or past the loop.",
        });
      }
    }
  }

  return issues;
}
